/**
 * Resolve a workflow run's `workflowId` from its run id alone.
 *
 * The canonical run detail page is keyed only by `runId` (`/runs/$runId`), but
 * the run/thoughts/diagnostics endpoints are nested under the workflow. This
 * hook bridges that gap via `GET /workflows/runs/:runId/lookup`. When the caller
 * already knows the workflowId (passed as a search param on internal
 * navigation), it short-circuits so no request is made.
 *
 * @module hooks/useRunLookup
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../config.js';
import { ApiError, toApiError } from '../lib/api-error';
import type { WrongOrgInfo } from '../ui/components/OrgSwitchPrompt';

interface RunLookupData {
  runId: string;
  workflowId: string;
}

interface RunLookupResponse {
  ok: boolean;
  data?: RunLookupData;
}

/**
 * @param runId - The workflow run id from the route
 * @param knownWorkflowId - Optional workflowId already in hand (skips the fetch)
 */
export function useRunLookup(runId: string | undefined, knownWorkflowId?: string) {
  const [wrongOrgInfo, setWrongOrgInfo] = React.useState<WrongOrgInfo | null>(null);

  const query = useQuery<RunLookupData>({
    queryKey: ['workflow-run-lookup', runId],
    enabled: Boolean(runId) && !knownWorkflowId,
    staleTime: Infinity, // run → workflow mapping never changes
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.details?.error === 'WORKFLOW_RUN_WRONG_ORG') {
        return false;
      }
      return failureCount < 2;
    },
    queryFn: async () => {
      if (!runId) throw new Error('Missing runId');
      const res = await fetch(`${API_URL}/workflows/runs/${runId}/lookup`, {
        credentials: 'include',
      });
      if (!res.ok) {
        const apiError = await toApiError(res, 'Failed to resolve run');
        const details = apiError.details;
        if (
          details?.error === 'WORKFLOW_RUN_WRONG_ORG' &&
          typeof details.targetOrgId === 'string' &&
          typeof details.targetOrgName === 'string'
        ) {
          setWrongOrgInfo({ orgId: details.targetOrgId, orgName: details.targetOrgName });
        }
        throw apiError;
      }
      setWrongOrgInfo(null);
      const json: RunLookupResponse = await res.json();
      if (!json.ok || !json.data) {
        throw new Error('Run not found');
      }
      return json.data;
    },
  });

  // Prefer the known workflowId (no request needed) over the fetched one.
  const workflowId = knownWorkflowId ?? query.data?.workflowId ?? null;

  const clearWrongOrgInfo = React.useCallback(() => setWrongOrgInfo(null), []);

  return {
    workflowId,
    isLoading: knownWorkflowId ? false : query.isLoading,
    isError: knownWorkflowId ? false : query.isError,
    error: query.error,
    wrongOrgInfo: knownWorkflowId ? null : wrongOrgInfo,
    clearWrongOrgInfo,
  };
}
